import { Car01, Check, ChevronDown } from "@untitledui/icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useOrganization } from "@/lib/organization/organization-context"
import { useDealershipsForBodyshop } from "@/lib/api/use-teams"
import { ORG_VISUAL_TOKENS } from "@/lib/team-types/visual-tokens"
import { cx } from "@/lib/utils/cx"

const DEALER_TOKENS = ORG_VISUAL_TOKENS.dealership

// Body-shop-only filter that narrows estimates to those originating from a
// chosen subset of connected dealerships. Hides itself when there's nothing
// to choose between (zero or one connected dealership).
//
// State is owned by the caller — the component is a pure controlled input
// over a string-id array. The estimates page persists selection via URL
// (`?dealerships=12,17`) so filtered views are bookmarkable + shareable; the
// SA dashboard reads the same param so cross-page navigation preserves the
// filter.

interface DealershipFilterProps {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}

export function DealershipFilter({ selectedIds, onChange }: DealershipFilterProps) {
  const { activeBodyShopId } = useOrganization()
  const { data: dealerships = [] } = useDealershipsForBodyshop(activeBodyShopId)

  if (dealerships.length < 2) return null

  const selectedSet = new Set(selectedIds)
  // Drop ids that no longer match a connected dealership (e.g., a stale URL
  // bookmark after a disconnect). Trigger label + chip stay accurate.
  const validSelected = dealerships.filter((d) => selectedSet.has(d.id))

  const triggerLabel =
    validSelected.length === 0
      ? "All dealerships"
      : validSelected.length === 1
        ? validSelected[0].name
        : `${validSelected[0].name} +${validSelected.length - 1}`

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const clear = () => onChange([])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cx(
            "h-10 px-3 rounded-xl border text-xs md:text-sm font-medium flex items-center gap-2 transition-all duration-150 shrink-0",
            validSelected.length > 0
              ? "border-sky-300 bg-sky-50/60 text-sky-900 hover:border-sky-400 hover:bg-sky-50"
              : "border-zinc-200 bg-zinc-50 text-zinc-700 hover:border-zinc-300 hover:bg-white",
          )}
        >
          <div className={cx("size-5 rounded-md grid place-items-center shrink-0 shadow-sm", DEALER_TOKENS.gradient, DEALER_TOKENS.shadow)}>
            <Car01 className="size-3 text-white" strokeWidth={2.5} aria-hidden />
          </div>
          <span className="truncate max-w-[140px]">{triggerLabel}</span>
          {validSelected.length > 1 && (
            <span className="size-4 rounded bg-sky-600 text-white text-[10px] font-bold grid place-items-center shrink-0 tabular-nums">
              {validSelected.length}
            </span>
          )}
          <ChevronDown className="size-3.5 text-current opacity-60 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[280px] p-0 rounded-2xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/15 overflow-hidden"
      >
        <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            Filter by dealership
          </p>
          {validSelected.length > 0 && (
            <button
              type="button"
              onClick={clear}
              className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto py-1.5">
          <div className="px-1.5 space-y-0.5">
            {dealerships.map((d) => {
              const checked = selectedSet.has(d.id)
              const cs = [d.city, d.state].filter(Boolean).join(", ")
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggle(d.id)}
                  className={cx(
                    "w-full flex items-center gap-3 p-2 rounded-xl transition-colors duration-100 text-left",
                    checked ? "bg-sky-50/60" : "hover:bg-zinc-50",
                  )}
                >
                  <div className={cx("size-9 rounded-xl grid place-items-center shrink-0 shadow-md", DEALER_TOKENS.gradient, DEALER_TOKENS.shadow)}>
                    <Car01 className="size-[18px] text-white" strokeWidth={2} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cx(
                        "text-[13px] font-semibold truncate",
                        checked ? "text-sky-900" : "text-zinc-900",
                      )}
                    >
                      {d.name}
                    </p>
                    <p
                      className={cx(
                        "text-[11px] truncate",
                        checked ? "text-sky-700/70" : "text-zinc-400",
                      )}
                    >
                      {cs || "Dealership"}
                    </p>
                  </div>
                  <span
                    className={cx(
                      "size-5 rounded-md border-2 grid place-items-center shrink-0 transition-all duration-150",
                      checked
                        ? "bg-sky-600 border-sky-600 shadow-sm shadow-sky-600/30"
                        : "border-zinc-300",
                    )}
                  >
                    {checked && (
                      <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
